import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, Grid, IconButton, Chip, 
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Alert, Checkbox, FormControlLabel, Divider, Tooltip
} from '@mui/material';
import { 
  Save, Refresh, AccessTime, People, 
  Clear, Delete
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const BulkAttendance = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedShift, setSelectedShift] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [shiftDetails, setShiftDetails] = useState(null);

  useEffect(() => {
    fetchShifts();
    fetchEmployees();
  }, []);

  const fetchShifts = async () => {
    try {
      const response = await api.get('/hr/shifts/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setShifts(data.filter(s => s.is_active));
    } catch (error) {
      toast.error('Failed to fetch shifts');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/hr/employees/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setEmployees(data);
    } catch (error) {
      toast.error('Failed to fetch employees');
    }
  };

  const handleShiftChange = (shiftId) => {
    setSelectedShift(shiftId);
    const shift = shifts.find(s => s.id === shiftId);
    setShiftDetails(shift || null);
    setSelectedEmployees([]);
    setAttendanceData([]);
  };

  const handleEmployeeSelect = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.id));
    }
  };

  const handleGenerateAttendance = () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    if (!selectedShift) {
      toast.error('Please select a shift');
      return;
    }

    const data = selectedEmployees.map(empId => {
      const employee = employees.find(e => e.id === empId);
      return {
        employee: empId,
        employee_name: employee?.full_name || employee?.employee_id || 'N/A',
        shift: selectedShift,
        date: selectedDate,
        status: 'present',
        check_in_time: shiftDetails?.start_time || '',
        check_out_time: shiftDetails?.end_time || '',
        overtime_hours: 0,
        late_minutes: 0,
        early_leave_minutes: 0,
        notes: '',
        is_new: true
      };
    });

    setAttendanceData(data);
    toast.success(`Attendance template generated for ${data.length} employees`);
  };

  const handleAttendanceChange = (index, field, value) => {
    const updated = [...attendanceData];
    updated[index][field] = value;
    setAttendanceData(updated);
  };

  const handleRemoveEmployee = (index) => {
    const updated = [...attendanceData];
    const removed = updated.splice(index, 1);
    setAttendanceData(updated);
    setSelectedEmployees(prev => prev.filter(id => id !== removed[0].employee));
    toast.info(`Removed ${removed[0].employee_name} from list`);
  };

  const handleSaveAll = async () => {
    if (attendanceData.length === 0) {
      toast.error('No attendance records to save');
      return;
    }

    setSaving(true);
    try {
      const saveData = attendanceData.map(record => ({
        employee: record.employee,
        shift: record.shift,
        date: record.date,
        status: record.status,
        check_in_time: record.check_in_time || null,
        check_out_time: record.check_out_time || null,
        overtime_hours: parseFloat(record.overtime_hours) || 0,
        late_minutes: parseInt(record.late_minutes) || 0,
        early_leave_minutes: parseInt(record.early_leave_minutes) || 0,
        notes: record.notes || ''
      }));

      const response = await api.post('/hr/attendance/bulk/', saveData);
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`Saved with errors: ${response.data.errors.join(', ')}`);
      } else {
        toast.success(`Successfully saved ${saveData.length} attendance records`);
      }
      
      setAttendanceData([]);
      setSelectedEmployees([]);
      setSelectedShift('');
      setShiftDetails(null);
      
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error('Failed to save attendance records');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (attendanceData.length > 0 && !window.confirm('Clear all unsaved attendance data?')) {
      return;
    }
    setAttendanceData([]);
    setSelectedEmployees([]);
    setSelectedShift('');
    setShiftDetails(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <People sx={{ mr: 1, verticalAlign: 'middle' }} />
          Bulk Attendance Marking
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => { fetchShifts(); fetchEmployees(); }}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Clear />}
            onClick={handleReset}
            sx={{ mr: 1 }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Save />}
            onClick={handleSaveAll}
            disabled={saving || attendanceData.length === 0}
          >
            {saving ? <CircularProgress size={24} /> : `Save All (${attendanceData.length})`}
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>How it works:</strong> Select a shift, choose multiple employees, generate attendance template, 
        edit individual timings if needed, then save all at once.
      </Alert>

      {/* Selection Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
          Select Shift & Employees
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Select Shift</InputLabel>
              <Select
                value={selectedShift}
                onChange={(e) => handleShiftChange(e.target.value)}
                label="Select Shift"
              >
                <MenuItem value="">None</MenuItem>
                {shifts.map((shift) => (
                  <MenuItem key={shift.id} value={shift.id}>
                    {shift.name} ({shift.start_time} - {shift.end_time})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGenerateAttendance}
              disabled={!selectedShift || selectedEmployees.length === 0}
              sx={{ height: 56 }}
            >
              Generate Attendance ({selectedEmployees.length} employees)
            </Button>
          </Grid>
        </Grid>

        {shiftDetails && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary">
              Shift Details: {shiftDetails.name}
            </Typography>
            <Typography variant="body2">
              Start: {shiftDetails.start_time} | End: {shiftDetails.end_time} | 
              Total Hours: {shiftDetails.total_hours}h | 
              Break: {shiftDetails.break_start || 'N/A'} - {shiftDetails.break_end || 'N/A'}
            </Typography>
          </Box>
        )}

        {/* Employee Selection */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Select Employees
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedEmployees.length === employees.length && employees.length > 0}
                      indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employees.length}
                      onChange={handleSelectAll}
                    />
                  }
                  label={`Select All (${employees.length})`}
                />
              </Grid>
              {employees.map((emp) => (
                <Grid item xs={12} sm={6} md={4} key={emp.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedEmployees.includes(emp.id)}
                        onChange={() => handleEmployeeSelect(emp.id)}
                      />
                    }
                    label={emp.full_name || emp.employee_id}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      </Paper>

      {/* Attendance Data Table */}
      {attendanceData.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Attendance Records ({attendanceData.length} employees)
            <Chip 
              label={`Shift: ${shiftDetails?.name || 'N/A'}`} 
              color="primary" 
              size="small" 
              sx={{ ml: 2 }}
            />
            <Chip 
              label={`Date: ${new Date(selectedDate).toLocaleDateString('en-IN')}`} 
              variant="outlined" 
              size="small" 
              sx={{ ml: 1 }}
            />
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Check In</TableCell>
                  <TableCell>Check Out</TableCell>
                  <TableCell align="center">Overtime</TableCell>
                  <TableCell align="center">Late (min)</TableCell>
                  <TableCell align="center">Early Leave</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceData.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {record.employee_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={record.status}
                          onChange={(e) => handleAttendanceChange(index, 'status', e.target.value)}
                        >
                          <MenuItem value="present">Present</MenuItem>
                          <MenuItem value="absent">Absent</MenuItem>
                          <MenuItem value="leave">Leave</MenuItem>
                          <MenuItem value="half_day">Half Day</MenuItem>
                          <MenuItem value="holiday">Holiday</MenuItem>
                          <MenuItem value="weekend">Weekend</MenuItem>
                          <MenuItem value="late">Late</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="time"
                        value={record.check_in_time || ''}
                        onChange={(e) => handleAttendanceChange(index, 'check_in_time', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="time"
                        value={record.check_out_time || ''}
                        onChange={(e) => handleAttendanceChange(index, 'check_out_time', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small"
                        type="number"
                        value={record.overtime_hours || 0}
                        onChange={(e) => handleAttendanceChange(index, 'overtime_hours', e.target.value)}
                        inputProps={{ step: 0.5, min: 0, style: { width: 60 } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small"
                        type="number"
                        value={record.late_minutes || 0}
                        onChange={(e) => handleAttendanceChange(index, 'late_minutes', e.target.value)}
                        inputProps={{ min: 0, style: { width: 60 } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small"
                        type="number"
                        value={record.early_leave_minutes || 0}
                        onChange={(e) => handleAttendanceChange(index, 'early_leave_minutes', e.target.value)}
                        inputProps={{ min: 0, style: { width: 60 } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove employee">
                        <IconButton size="small" color="error" onClick={() => handleRemoveEmployee(index)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default BulkAttendance;